import type { ServerNode } from "./types";
import type { ActionNodeData } from "../nodes/ActionNode";
import { ACTION_CODE_TO_FORM_TYPE, actionCodeToTitle } from "../utils/actionCodeMapping";
import { castToString, castToStringArray } from "./configCastHelpers";

export function hydrateActionData(node: ServerNode): ActionNodeData {
    const { config, type } = node;
    const formType = ACTION_CODE_TO_FORM_TYPE[type];

    const base: ActionNodeData = {
        actionCode: type,
        actionType: formType ?? type,
        title: actionCodeToTitle(type) ?? "Action",
        isPlaceholder: false,
    };

    switch (type) {
        case "Action.Message.AutomatedResponse": {
            const mediaFileList = Array.isArray(config.mediaFileList) ? config.mediaFileList : [];
            return {
                ...base,
                message: castToString(config.messageBody ?? config.message),
                templateId: castToString(config.templateId),
                mediaFileList,
                hasMedia: mediaFileList.length > 0,
                components: config.components,
                fieldNameAndValueMap:
                    config.fieldNameAndValueMap != null && typeof config.fieldNameAndValueMap === "object"
                        ? (config.fieldNameAndValueMap as Record<string, unknown>)
                        : undefined,
            };
        }

        case "Action.Crm.CreateLead":
            return { ...base };

        case "Action.Crm.AddNote":
            return {
                ...base,
                logRecordsFor: castToString(config.note) ?? "all",
                logRecordAsThread: config.shouldThreadActivities === true ? "true" : "false",
            };

        case "Action.Crm.LogActivity":
            return {
                ...base,
                logRecordsFor: castToString(config.note) ?? "all",
                logRecordAsThread: config.shouldThreadActivities === true ? "true" : "false",
            };

        case "Action.Notify.NotifyUser":
            return {
                ...base,
                recordOwner: config.includeRecordOwner === true,
                accountOwner: config.includeAccountOwner === true,
                includeAdditionalEmails: (castToStringArray(config.customEmails)?.length ?? 0) > 0,
                additionalEmails: castToStringArray(config.customEmails),
            };

        case "Action.FlowControl.Delay": {
            const amount = typeof config.delayAmount === "number" ? config.delayAmount : 0;
            const unit = castToString(config.delayUnit)?.toLowerCase() ?? "minutes";
            const totalMinutes =
                unit === "days" ? amount * 24 * 60 : unit === "hours" ? amount * 60 : amount;
            const delayDays = Math.floor(totalMinutes / (24 * 60));
            const delayHours = Math.floor((totalMinutes % (24 * 60)) / 60);
            const delayMinutes = Math.floor(totalMinutes % 60);
            return {
                ...base,
                delayDays,
                delayHours,
                delayMinutes,
            };
        }

        default:
            return base;
    }
}
